import dayjs from 'dayjs';
import type {
  ApplicantMessage,
  ApplicantMessagesResponse,
  CommunicationMedium,
} from '../services/types/applicant.types';

const COMMUNICATION_MEDIUMS: CommunicationMedium[] = ['WHATSAPP', 'EMAIL', 'ZALO'];

export const formatTime = (sentTime: string): string => {
  try {
    const parts = sentTime.split(':');
    if (parts.length < 2) return sentTime;
    const hour = parseInt(parts[0], 10);
    const minutes = parts[1];
    if (isNaN(hour) || !minutes) return sentTime;
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}.${minutes} ${ampm}`;
  } catch {
    return sentTime;
  }
};

export const isWithinCurrentWeek = (sendDate: string): boolean => {
  try {
    const messageDate = dayjs(sendDate).startOf('day');
    const today = dayjs().startOf('day');
    const sevenDaysAgo = dayjs().subtract(7, 'days').startOf('day');

    return (
      messageDate.isSame(today) ||
      (messageDate.isAfter(sevenDaysAgo) && messageDate.isBefore(today))
    );
  } catch {
    return false;
  }
};

export const formatDateSeparator = (sendDate: string): string => {
  try {
    const messageDate = dayjs(sendDate);
    if (!messageDate.isValid()) return sendDate;
    const today = dayjs().startOf('day');
    const yesterday = dayjs().subtract(1, 'day').startOf('day');
    const messageDateStart = messageDate.startOf('day');

    if (messageDateStart.isSame(today)) {
      return 'Today';
    }
    if (messageDateStart.isSame(yesterday)) {
      return 'Yesterday';
    }
    if (isWithinCurrentWeek(sendDate)) {
      return messageDate.format('dddd');
    }
    return messageDate.format('dddd, MMMM DD');
  } catch {
    return sendDate;
  }
};

export const formatMessageDateTime = (
  sendDate: string,
  sentTime: string,
  isWithinWeek: boolean,
): string => {
  try {
    if (isWithinWeek) {
      const messageDate = dayjs(sendDate);
      const today = dayjs().startOf('day');
      const yesterday = dayjs().subtract(1, 'day').startOf('day');
      const messageDateStart = messageDate.startOf('day');

      let dayLabel = messageDate.format('dddd');
      if (messageDateStart.isSame(today)) {
        dayLabel = 'Today';
      } else if (messageDateStart.isSame(yesterday)) {
        dayLabel = 'Yesterday';
      }

      return `${dayLabel} ${formatTime(sentTime)}`;
    }
    const messageDate = dayjs(sendDate);
    return `${messageDate.format('MM/DD')} ${formatTime(sentTime)}`;
  } catch {
    return formatTime(sentTime);
  }
};

export const groupMessagesByDate = (
  messages: ApplicantMessage[],
): Record<string, ApplicantMessage[]> => {
  const groups: Record<string, ApplicantMessage[]> = {};

  messages.forEach((message) => {
    const dateKey = message.send_date.split('T')[0];
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);
  });

  return groups;
};

/** Supports wrapped API payloads and legacy bare message arrays. */
/** Parses API medium values (plain string, WHATSAPP enum, or sql.NullString `{ String, Valid }`). */
export function parseCommunicationMedium(value: unknown): CommunicationMedium | null {
  if (value == null) return null;

  let raw = '';
  if (typeof value === 'string') {
    raw = value;
  } else if (typeof value === 'object') {
    const record = value as { String?: string; Valid?: boolean };
    if ('Valid' in record && record.Valid === false) {
      return null;
    }
    if ('String' in record) {
      raw = record.String ?? '';
    } else {
      return null;
    }
  } else {
    return null;
  }

  const normalized = raw.trim().toUpperCase();
  if (COMMUNICATION_MEDIUMS.includes(normalized as CommunicationMedium)) {
    return normalized as CommunicationMedium;
  }
  return null;
}

/** Reads `communicationMedium` / `communication_medium` from `GET /search/{id}/basic` payload. */
export function communicationMediumFromBasicInfo(
  data: Record<string, unknown>,
): CommunicationMedium | null {
  return (
    parseCommunicationMedium(data.communicationMedium) ??
    parseCommunicationMedium(data.communication_medium) ??
    null
  );
}

export function normalizeApplicantBasicInfo<T extends Record<string, unknown>>(data: T): T & {
  communication_medium?: CommunicationMedium;
} {
  const medium = communicationMediumFromBasicInfo(data);
  if (!medium) {
    return data as T & { communication_medium?: CommunicationMedium };
  }
  return { ...data, communication_medium: medium };
}

/** Label for Additional Info manual interview init (`POST /manual-interview-init`). */
export function getManualInterviewInitButtonLabel(
  medium: CommunicationMedium | null | undefined,
  isLoading: boolean,
): string {
  if (isLoading) {
    return medium === 'EMAIL' ? 'Allocating...' : 'Triggering...';
  }
  return medium === 'EMAIL' ? 'Allocate Questions' : 'Trigger Question';
}

export function normalizeApplicantMessagesResponse(
  payload: ApplicantMessagesResponse | ApplicantMessage[],
): ApplicantMessagesResponse {
  if (Array.isArray(payload)) {
    return { messages: payload };
  }
  return {
    applicantId: payload.applicantId,
    communicationMedium: payload.communicationMedium ?? null,
    messages: payload.messages ?? [],
    canSendManualWelcome: payload.canSendManualWelcome ?? false,
    manualWelcomeReason: payload.manualWelcomeReason ?? '',
  };
}

/**
 * Welcome send is a WhatsApp action. The backend (`canSendManualWelcome`) decides whether it is
 * allowed, so an existing thread does not hide it: a candidate who only got a welcome email has
 * messages but still needs the WhatsApp welcome. Any medium other than WhatsApp, or an unknown
 * medium, hides the button.
 */
export function shouldShowSendWelcome(
  _messages: Pick<ApplicantMessage, 'communication_medium'>[],
  communicationMedium?: CommunicationMedium | null,
): boolean {
  return Boolean(communicationMedium) && communicationMedium!.toUpperCase() === 'WHATSAPP';
}

export const groupMessagesByMedium = (
  messages: ApplicantMessage[],
): Record<CommunicationMedium, ApplicantMessage[]> => {
  const groups: Record<CommunicationMedium, ApplicantMessage[]> = {
    WHATSAPP: [],
    EMAIL: [],
    ZALO: [],
  };

  messages.forEach((message) => {
    const medium = message.communication_medium;
    if (COMMUNICATION_MEDIUMS.includes(medium)) {
      groups[medium].push(message);
    }
  });

  return groups;
};

/** Hour:minute key for burst grouping (UI shows minute precision, not seconds). */
export const getSentTimeMinuteKey = (sentTime: string): string => {
  const parts = sentTime.split(':');
  if (parts.length < 2) return sentTime;
  return `${parts[0]}:${parts[1]}`;
};

export const isSameMessageBurst = (
  prev: ApplicantMessage | null,
  curr: ApplicantMessage,
): boolean =>
  !!prev &&
  prev.sender_type === curr.sender_type &&
  getSentTimeMinuteKey(prev.sent_time) === getSentTimeMinuteKey(curr.sent_time);

/** True when curr is the last message in a same-sender, same-minute run. */
export const isLastInMessageBurst = (
  curr: ApplicantMessage,
  next: ApplicantMessage | null,
): boolean => !next || !isSameMessageBurst(curr, next);

export const sortMessagesByDateTime = (
  messages: ApplicantMessage[],
): ApplicantMessage[] =>
  [...messages].sort((a, b) => {
    const dateA = `${a.send_date.split('T')[0]}T${a.sent_time}`;
    const dateB = `${b.send_date.split('T')[0]}T${b.sent_time}`;
    if (dateA !== dateB) {
      return dateA < dateB ? -1 : 1;
    }
    return a.id - b.id;
  });

export const getInitials = (name: string): string => {
  const trimmed = name?.trim();
  if (!trimmed) {
    return '?';
  }
  const parts = trimmed.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  return trimmed.charAt(0).toUpperCase();
};

export type WhatsAppTextSegment =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; children: WhatsAppTextSegment[] }
  | { kind: 'italic'; children: WhatsAppTextSegment[] }
  | { kind: 'strike'; children: WhatsAppTextSegment[] }
  | { kind: 'mono'; children: WhatsAppTextSegment[] };

const WHATSAPP_FORMAT_DELIMITERS: {
  kind: WhatsAppTextSegment['kind'];
  open: string;
  close: string;
}[] = [
  { kind: 'mono', open: '```', close: '```' },
  { kind: 'bold', open: '*', close: '*' },
  { kind: 'italic', open: '_', close: '_' },
  { kind: 'strike', open: '~', close: '~' },
];

/**
 * Parses WhatsApp-style inline formatting (*bold*, _italic_, ~strike~, ```mono```).
 */
export function parseWhatsAppFormattedText(text: string): WhatsAppTextSegment[] {
  if (!text) {
    return [];
  }

  const segments: WhatsAppTextSegment[] = [];
  let index = 0;

  while (index < text.length) {
    let matched = false;

    for (const rule of WHATSAPP_FORMAT_DELIMITERS) {
      if (!text.startsWith(rule.open, index)) {
        continue;
      }
      const contentStart = index + rule.open.length;
      const closeIndex = text.indexOf(rule.close, contentStart);
      if (closeIndex === -1 || closeIndex === contentStart) {
        continue;
      }
      const inner = text.slice(contentStart, closeIndex);
      segments.push({
        kind: rule.kind,
        children: parseWhatsAppFormattedText(inner),
      } as WhatsAppTextSegment);
      index = closeIndex + rule.close.length;
      matched = true;
      break;
    }

    if (matched) {
      continue;
    }

    let nextSpecial = text.length;
    for (const rule of WHATSAPP_FORMAT_DELIMITERS) {
      const pos = text.indexOf(rule.open, index + 1);
      if (pos !== -1 && pos < nextSpecial) {
        nextSpecial = pos;
      }
    }
    const plain = text.slice(index, nextSpecial);
    if (plain) {
      segments.push({ kind: 'text', text: plain });
    }
    index = nextSpecial;
  }

  return segments;
}

export function resolveQuotedMessage(
  messages: ApplicantMessage[],
  message: ApplicantMessage,
): ApplicantMessage | null {
  if (message.parent_id != null && message.parent_id !== '') {
    const parentId =
      typeof message.parent_id === 'string'
        ? parseInt(message.parent_id, 10)
        : message.parent_id;
    if (!Number.isNaN(parentId)) {
      const byParent = messages.find((m) => m.id === parentId);
      if (byParent) {
        return byParent;
      }
    }
  }

  if (message.quote_message_id) {
    return messages.find((m) => m.message_id === message.quote_message_id) ?? null;
  }

  return null;
}

/** Single-line preview for quoted message bubbles */
export function getQuotePreviewText(message: ApplicantMessage): string {
  if (message.is_deleted) {
    return 'This message was deleted';
  }
  if (message.message_text?.trim()) {
    const singleLine = message.message_text.replace(/\s+/g, ' ').trim();
    return singleLine.length > 120 ? `${singleLine.slice(0, 117)}...` : singleLine;
  }
  if (message.message_type && message.message_type !== 'TEXT') {
    return message.message_type.charAt(0) + message.message_type.slice(1).toLowerCase();
  }
  return 'Message';
}

export const getFilenameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/');
    const filename = segments[segments.length - 1];
    return filename ? decodeURIComponent(filename) : 'Download Document';
  } catch {
    return 'Download Document';
  }
};
